// The library on the API (W2): the catalogue (GET /v1/library, /v1/library/{id}), its authors
// (/v1/authors, /v1/authors/{id}), the reader's entitlements (GET /v1/entitlements), covers and
// packages, and a web purchase (POST /v1/purchases/stripe/checkout). On the prototype data the same
// shapes are built from the static files in public/library/, so the pages are the same on both.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext } from "react";
import { createApi } from "../api/client";
import type { components } from "../api/schema";
import { ok } from "./api";
import { ApiError } from "./problem";

type S = components["schemas"];
/**
 * A catalogue entry. `words` and `spineTitle` (API-7, FS-371 D2/D3: what a spine is thick by and titled) are
 * served by the API since BS-API-FEAT-7 but predate the vendored contract in openapi.json, so they are read
 * here as optional until the contract is vendored again.
 */
export type LibraryEntry = S["LibraryEntry"] & { words?: number | null; spineTitle?: string | null };
export type ApiAuthor = S["Author"];
export type Entitlement = S["Entitlement"];

/** The World English Bible, served from public/bible/web (not a package): the Bible quoted everywhere on Fellowship. */
export const WEB_ID = "eng-web";
export const WEB_ENTRY: LibraryEntry = {
  id: WEB_ID,
  kind: "bible",
  title: "World English Bible",
  shortTitle: "WEB",
  author: null,
  authorIds: [],
  language: "en",
  year: "2000",
  summary: "A modern public-domain translation, the Bible Fellowship quotes.",
  access: "free",
  rights: "cleared",
  available: true,
  productIds: [],
  cover: null,
  packageSize: null,
  packageSha256: null,
  packageBuiltAt: null,
  binding: null,
  bundled: false,
  setId: null,
};

export interface LibrarySource {
  /** True on the API: packages are read in the browser; false on the prototype (static text only). */
  readonly live: boolean;
  entries(): Promise<LibraryEntry[]>;
  entry(id: string): Promise<LibraryEntry | null>;
  authors(): Promise<ApiAuthor[]>;
  author(id: string): Promise<ApiAuthor | null>;
  /** The signed-in reader's entitlements; none when signed out. */
  entitlements(): Promise<Entitlement[]>;
  /** A Stripe Checkout page for one product: where to send the reader. */
  checkout(productKey: string): Promise<string>;
  coverUrl(entry: Pick<LibraryEntry, "id" | "cover">): string | null;
  /** The package for ranged reads, or null where packages aren't read (the prototype, the WEB). */
  packageOf(entry: Pick<LibraryEntry, "id" | "packageSha256">): { key: string; url: string } | null;
}

const base = () => ((import.meta.env?.BASE_URL as string | undefined) ?? "/").replace(/\/?$/, "/");

export function createApiLibrary(opts: { baseUrl: string; fetch?: typeof fetch }): LibrarySource {
  const api = createApi(opts);
  return {
    live: true,
    async entries() {
      const r = await ok(api.GET("/v1/library"));
      return [...r.items, WEB_ENTRY];
    },
    async entry(id) {
      if (id === WEB_ID) return WEB_ENTRY;
      try {
        return await ok(api.GET("/v1/library/{id}", { params: { path: { id } } }));
      } catch (e) {
        if (e instanceof ApiError && (e.status === 404 || e.status === 400)) return null;
        throw e;
      }
    },
    async authors() {
      return (await ok(api.GET("/v1/authors"))).items;
    },
    async author(id) {
      try {
        return await ok(api.GET("/v1/authors/{id}", { params: { path: { id } } }));
      } catch (e) {
        if (e instanceof ApiError && (e.status === 404 || e.status === 400)) return null;
        throw e;
      }
    },
    async entitlements() {
      try {
        return (await ok(api.GET("/v1/entitlements"))).items;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return [];
        throw e;
      }
    },
    async checkout(productKey) {
      // An Idempotency-Key makes a double click one Checkout session (the API accepts it on creating POSTs).
      return (await ok(api.POST("/v1/purchases/stripe/checkout", { body: { productKey }, headers: { "idempotency-key": crypto.randomUUID() } }))).url;
    },
    // Covers and packages go through the base the app talks to (the dev proxy locally), so they're same-origin.
    coverUrl: (e) => (e.cover ? `${opts.baseUrl}/v1/library/${encodeURIComponent(e.id)}/cover` : null),
    packageOf: (e) =>
      e.id === WEB_ID
        ? null
        : {
            key: `${e.id}-${(e.packageSha256 ?? "x").slice(0, 12)}`,
            url: `${opts.baseUrl}/v1/library/${encodeURIComponent(e.id)}/package${e.packageSha256 ? `?v=${e.packageSha256.slice(0, 12)}` : ""}`,
          },
  };
}

type StaticWork = {
  id: string;
  title: string;
  shortTitle: string;
  kind: string;
  author: string;
  authorIds: string[];
  access: "free" | "paid";
  written: string | null;
  language: string | null;
  text: boolean;
  /** "jacket" or null (catalogue.json since 2026-10-14). */
  binding?: string | null;
  bundled?: boolean;
  set?: { id: string; index: number } | null;
  words?: number | null;
  spineTitle?: string | null;
};
type StaticAuthor = { id: string; name: string; dates: string | null; bio: string | null; facets: string[]; tone: string; works: { libraryId: string }[] };

async function staticJson<T>(path: string): Promise<T> {
  const r = await fetch(`${base()}library/${path}`);
  if (!r.ok) throw new ApiError("This part of the library isn’t available just now.", "static", r.status);
  return (await r.json()) as T;
}

/** The prototype: the app's own catalogue, as built into public/library (scripts/build-books.mjs). */
export function createMockLibrary(): LibrarySource {
  const toEntry = (w: StaticWork): LibraryEntry => ({
    id: w.id,
    kind: w.kind as LibraryEntry["kind"],
    title: w.title,
    shortTitle: w.shortTitle,
    author: w.author || null,
    authorIds: w.authorIds,
    language: w.language,
    year: w.written,
    summary: null,
    access: w.access,
    rights: "cleared",
    available: true,
    productIds: [],
    cover: null,
    packageSize: null,
    packageSha256: null,
    packageBuiltAt: null,
    binding: w.binding ?? null,
    bundled: w.bundled ?? false,
    setId: w.set?.id ?? null,
    words: w.words ?? null,
    spineTitle: w.spineTitle ?? null,
  });
  const works = () => staticJson<{ works: StaticWork[] }>("catalogue.json").then((c) => c.works);
  const authors = () =>
    staticJson<{ authors: StaticAuthor[] }>("authors.json").then((a) =>
      a.authors.map((x): ApiAuthor => ({ id: x.id, name: x.name, dates: x.dates, bio: x.bio, facets: x.facets, tone: x.tone, works: x.works.map((w) => w.libraryId) })),
    );
  return {
    live: false,
    entries: async () => [...(await works()).map(toEntry), WEB_ENTRY],
    entry: async (id) => (id === WEB_ID ? WEB_ENTRY : ((await works()).map(toEntry).find((e) => e.id === id) ?? null)),
    authors,
    author: async (id) => (await authors()).find((a) => a.id === id) ?? null,
    entitlements: async () => [],
    checkout: async () => {
      throw new ApiError("Purchases on the web aren’t open in this preview.", "feature-closed", 503);
    },
    coverUrl: () => null,
    packageOf: () => null,
  };
}

export const LibraryContext = createContext<LibrarySource | null>(null);
const fallback = createMockLibrary();
export const useLibrarySource = () => useContext(LibraryContext) ?? fallback;

export const libraryKeys = {
  entries: ["library", "entries"] as const,
  entry: (id: string) => ["library", "entry", id] as const,
  authors: ["library", "authors"] as const,
  author: (id: string) => ["library", "author", id] as const,
  entitlements: ["library", "entitlements"] as const,
};

export function useLibraryEntries() {
  const s = useLibrarySource();
  return useQuery({ queryKey: libraryKeys.entries, queryFn: () => s.entries(), staleTime: 5 * 60_000 });
}

/** One entry, fresh from /v1/library/{id} (its availability can change with a rights switch). */
export function useLibraryEntry(id: string | null | undefined) {
  const s = useLibrarySource();
  const list = useLibraryEntries();
  return useQuery({
    queryKey: libraryKeys.entry(id ?? ""),
    queryFn: () => s.entry(id!),
    enabled: !!id,
    staleTime: 60_000,
    placeholderData: () => list.data?.find((e) => e.id === id),
  });
}

export function useApiAuthors() {
  const s = useLibrarySource();
  return useQuery({ queryKey: libraryKeys.authors, queryFn: () => s.authors(), staleTime: 60 * 60_000 });
}

export function useApiAuthor(id: string | null | undefined) {
  const s = useLibrarySource();
  const list = useApiAuthors();
  return useQuery({
    queryKey: libraryKeys.author(id ?? ""),
    queryFn: () => s.author(id!),
    enabled: !!id,
    staleTime: 60 * 60_000,
    placeholderData: () => list.data?.find((a) => a.id === id),
  });
}

export function useEntitlements(opts: { poll?: number | false } = {}) {
  const s = useLibrarySource();
  return useQuery({ queryKey: libraryKeys.entitlements, queryFn: () => s.entitlements(), staleTime: 30_000, refetchInterval: opts.poll ?? false });
}

const BEFORE = "bs.checkout.before";

/** What the reader owned when they left for Checkout, so the return page knows what's new. */
export function entitlementsBeforeCheckout(): Set<string> | null {
  try {
    const raw = sessionStorage.getItem(BEFORE);
    return raw ? new Set(JSON.parse(raw) as string[]) : null;
  } catch {
    return null;
  }
}

export function useCheckout() {
  const s = useLibrarySource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (productKey: string) => s.checkout(productKey),
    onSuccess: (url) => {
      try {
        const had = qc.getQueryData<Entitlement[]>(libraryKeys.entitlements) ?? [];
        sessionStorage.setItem(BEFORE, JSON.stringify(had.map((e) => e.key)));
      } catch {
        // Private window: the return page compares with what it first sees.
      }
      window.location.assign(url);
    },
    meta: { inline: true },
  });
}

// ---- What a reader can do with an entry ----

/** free: read it here · owned: bought · get: buy on the web · app: in the app only · unavailable: rights pending · reference: free in the app, text not on the web (prototype) */
export type AccessState = "free" | "owned" | "get" | "app" | "unavailable" | "reference";

/** Whether an entitlement covers the entry: by its library id, or by a product that unlocks it. */
export function owns(entry: Pick<LibraryEntry, "id" | "productIds">, ents: Entitlement[] | undefined): boolean {
  if (!ents?.length) return false;
  const keys = new Set([entry.id, ...entry.productIds.map((p) => p.key)]);
  const now = Date.now();
  return ents.some((e) => keys.has(e.key) && (e.expiresAt === null || e.expiresAt > now));
}

export function accessState(
  entry: Pick<LibraryEntry, "id" | "access" | "available" | "productIds">,
  ents: Entitlement[] | undefined,
  opts: { purchases: boolean; webText?: boolean },
): AccessState {
  if (!entry.available) return "unavailable";
  if (entry.access === "free") return opts.webText === false ? "reference" : "free";
  if (owns(entry, ents)) return "owned";
  return opts.purchases && entry.productIds.length > 0 ? "get" : "app";
}

export const ACCESS_LINE: Record<AccessState, string> = {
  free: "Free to read",
  owned: "In your library",
  get: "Get it",
  app: "Available in the Bible Standard app",
  unavailable: "Not available yet",
  reference: "Free in the Bible Standard app",
};

export const UNAVAILABLE_NOTE = "The rights to this edition are still being cleared, so it can’t be read here yet. It will open as soon as they are.";

/** Languages by their code, for the Bibles section. */
export const LANGUAGE: Record<string, string> = {
  en: "English",
  ar: "Arabic",
  zh: "Chinese",
  nl: "Dutch",
  fr: "French",
  de: "German",
  grc: "Greek",
  he: "Hebrew",
  it: "Italian",
  ko: "Korean",
  la: "Latin",
  pt: "Portuguese",
  ru: "Russian",
  es: "Spanish",
};
